import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../services/api";

export default function Users() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [users, setUsers]             = useState([]);
  const [search, setSearch]           = useState("");
  const [roleFilter, setRoleFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading]         = useState(true);
  const [trashingId, setTrashingId]   = useState(null); // ← renamed from deletingId
  const [confirmId, setConfirmId]     = useState(null);
  const [togglingId, setTogglingId]   = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editUser, setEditUser]       = useState(null);
  const [editForm, setEditForm]       = useState({ name:"", email:"", role:"" });
  const [updating, setUpdating]       = useState(false);

  useEffect(() => {
    const r = searchParams.get("role");
    if (r) setRoleFilter(r);
  }, [searchParams]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data.users || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  // ── SOFT DELETE → moves to Trash ─────────────────────────────
  const trashUser = async (id) => {
    setTrashingId(id);
    try {
      await api.put(`/admin/user/${id}/trash`);  // ← PUT not DELETE
      setUsers(prev => prev.filter(u => u._id !== id));
    } catch (e) { console.error(e); alert("Failed to move to trash"); }
    finally { setTrashingId(null); setConfirmId(null); }
  };

  // ── TOGGLE ACTIVE / INACTIVE ─────────────────────────────────
  const toggleActive = async (user) => {
    setTogglingId(user._id);
    try {
      const ep = user.isActive !== false
        ? `/admin/user/${user._id}/deactivate`
        : `/admin/user/${user._id}/activate`;
      await api.put(ep);
      setUsers(prev => prev.map(u =>
        u._id === user._id ? { ...u, isActive: !u.isActive } : u
      ));
    } catch (e) { console.error(e); }
    finally { setTogglingId(null); }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({ name: user.name, email: user.email, role: user.role });
  };

  const updateUser = async () => {
    setUpdating(true);
    try {
      await api.put(`/admin/user/${editUser._id}`, editForm);
      setEditUser(null);
      fetchUsers();
    } catch (e) { console.error(e); }
    finally { setUpdating(false); }
  };

  const filteredUsers = users.filter(u => {
    const mS = u.name.toLowerCase().includes(search.toLowerCase()) ||
               u.email.toLowerCase().includes(search.toLowerCase());
    const mR = roleFilter === "all" || u.role === roleFilter;
    const mA = statusFilter === "all"
      ? true
      : statusFilter === "active"
        ? u.isActive !== false
        : u.isActive === false;
    return mS && mR && mA;
  });

  const activeCount   = users.filter(u => u.isActive !== false).length;
  const inactiveCount = users.filter(u => u.isActive === false).length;

  const roleTabs = [
    { key:"all",       label:"All",        count: users.length },
    { key:"carpenter", label:"Carpenters",  count: users.filter(u=>u.role==="carpenter").length },
    { key:"labour",    label:"Labours",     count: users.filter(u=>u.role==="labour").length },
    { key:"admin",     label:"Admins",      count: users.filter(u=>u.role==="admin").length },
  ];

  const statusTabs = [
    { key:"active",   label:"Active",   count:activeCount,   color:"#059669", bg:"#D1FAE5", border:"#A7F3D0" },
    { key:"inactive", label:"Inactive", count:inactiveCount, color:"#DC2626", bg:"#FEF2F2", border:"#FECACA" },
    { key:"all",      label:"All",      count:users.length,  color:"#78716C", bg:"#F5F5F4", border:"#E7E5E4" },
  ];

  const roleStyle = role => {
    if (role==="admin")     return { bg:"#EDE9FE", color:"#7C3AED", border:"#DDD6FE" };
    if (role==="carpenter") return { bg:"#FEF3C7", color:"#D97706", border:"#FDE68A" };
    if (role==="labour")    return { bg:"#E0F2FE", color:"#0369A1", border:"#BAE6FD" };
    return { bg:"#F3F4F6", color:"#6B7280", border:"#E5E7EB" };
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
        @keyframes popIn  { from{opacity:0;transform:scale(0.9)} to{opacity:1;transform:scale(1)} }
        *,*::before,*::after { box-sizing:border-box; margin:0; padding:0; }
        body { font-family:'Plus Jakarta Sans',sans-serif; background:#FAF7F4; }
        .sidebar { width:220px; min-height:100vh; flex-shrink:0; background:#fff; border-right:1.5px solid #E7E5E4; padding:24px 14px; display:flex; flex-direction:column; gap:6px; position:sticky; top:0; height:100vh; overflow-y:auto; }
        .topbar { display:none; }
        .hamburger { display:none; align-items:center; justify-content:center; width:40px; height:40px; border-radius:10px; border:1.5px solid #E7E5E4; background:#fff; cursor:pointer; flex-shrink:0; }
        .sidebar-overlay { display:none; position:fixed; inset:0; background:rgba(0,0,0,.4); z-index:40; }
        .nav-btn { display:flex; align-items:center; gap:9px; width:100%; padding:10px 12px; border-radius:10px; border:none; background:none; font-family:'Plus Jakarta Sans',sans-serif; font-size:14px; font-weight:600; color:#78716C; cursor:pointer; transition:background .15s,color .15s; text-align:left; }
        .nav-btn:hover,.nav-btn.active { background:#FEF3C7; color:#D97706; }
        .nav-btn.danger:hover { background:#FEF2F2; color:#DC2626; }
        .user-row { transition:background .15s; }
        .user-row:hover { background:#FFFBF7; }
        .tab-btn { padding:7px 14px; border-radius:20px; border:1.5px solid #E7E5E4; background:#fff; font-family:'Plus Jakarta Sans',sans-serif; font-size:12px; font-weight:700; color:#78716C; cursor:pointer; transition:all .15s; white-space:nowrap; display:flex; align-items:center; gap:5px; }
        .tab-btn:hover { border-color:#D97706; color:#D97706; }
        .tab-btn.active { background:#FEF3C7; border-color:#D97706; color:#92400E; }
        .search-input { width:100%; padding:11px 16px 11px 40px; background:#fff; border:1.5px solid #E7E5E4; border-radius:12px; font-family:'Plus Jakarta Sans',sans-serif; font-size:14px; color:#1C1917; outline:none; transition:border-color .2s; }
        .search-input:focus { border-color:#D97706; box-shadow:0 0 0 3px rgba(217,119,6,.1); }
        .search-input::placeholder { color:#A8A29E; }
        .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:100; display:flex; align-items:center; justify-content:center; padding:16px; }
        .modal-card { background:#fff; border-radius:20px; padding:28px; max-width:420px; width:100%; box-shadow:0 24px 64px rgba(0,0,0,.25); animation:popIn .25s ease; }
        .mobile-cards { display:none; }
        @media (max-width:768px) {
          .sidebar { position:fixed; left:0; top:0; z-index:50; transform:translateX(-100%); transition:transform .28s ease; height:100%; }
          .sidebar.open { transform:translateX(0); }
          .sidebar-overlay.open { display:block; }
          .topbar { display:flex; align-items:center; gap:12px; padding:14px 16px; background:#fff; border-bottom:1.5px solid #E7E5E4; position:sticky; top:0; z-index:30; }
          .hamburger { display:flex; }
          .main-wrap { flex-direction:column !important; }
          .main-content { padding:16px !important; }
          .desktop-table { display:none !important; }
          .mobile-cards { display:block; }
        }
        @media (min-width:769px) and (max-width:1100px) { .sidebar { width:190px; } }
      `}</style>

      {/* EDIT MODAL */}
      {editUser && (
        <div className="modal-overlay" onClick={() => setEditUser(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"20px" }}>
              <div style={{ width:"40px", height:"40px", borderRadius:"10px", background:"#FEF3C7", border:"1.5px solid #FDE68A", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"20px" }}>✏️</div>
              <div>
                <h3 style={{ fontSize:"17px", fontWeight:"800", color:"#1C1917" }}>Edit User</h3>
                <p style={{ fontSize:"12px", color:"#A8A29E" }}>Update user information</p>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:"14px", marginBottom:"20px" }}>
              {[{label:"Full Name",key:"name",type:"text"},{label:"Email",key:"email",type:"email"}].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize:"12px", fontWeight:"700", color:"#44403C", display:"block", marginBottom:"6px" }}>{f.label}</label>
                  <input type={f.type} value={editForm[f.key]} onChange={e => setEditForm({...editForm,[f.key]:e.target.value})}
                    style={{ width:"100%", padding:"11px 14px", border:"1.5px solid #E7E5E4", borderRadius:"10px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"14px", color:"#1C1917", outline:"none", background:"#FFF7ED" }}
                    onFocus={e=>{e.target.style.borderColor="#D97706";e.target.style.boxShadow="0 0 0 3px rgba(217,119,6,.1)"}}
                    onBlur={e=>{e.target.style.borderColor="#E7E5E4";e.target.style.boxShadow="none"}} />
                </div>
              ))}
              <div>
                <label style={{ fontSize:"12px", fontWeight:"700", color:"#44403C", display:"block", marginBottom:"6px" }}>Role</label>
                <select value={editForm.role} onChange={e => setEditForm({...editForm,role:e.target.value})}
                  style={{ width:"100%", padding:"11px 14px", border:"1.5px solid #E7E5E4", borderRadius:"10px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"14px", color:"#1C1917", outline:"none", background:"#FFF7ED", cursor:"pointer" }}>
                  <option value="carpenter">🪚 Carpenter</option>
                  <option value="labour">🔨 Labour</option>
                  <option value="admin">🛡️ Admin</option>
                </select>
              </div>
            </div>
            <div style={{ display:"flex", gap:"10px" }}>
              <button onClick={() => setEditUser(null)} style={{ flex:1, padding:"12px", borderRadius:"12px", border:"1.5px solid #E7E5E4", background:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"14px", fontWeight:"700", color:"#78716C", cursor:"pointer" }}>Cancel</button>
              <button onClick={updateUser} disabled={updating} style={{ flex:1, padding:"12px", borderRadius:"12px", border:"none", background:"#D97706", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"14px", fontWeight:"700", color:"#fff", cursor:"pointer", opacity:updating?.7:1 }}>
                {updating?"Updating…":"Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TRASH CONFIRM MODAL */}
      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth:"380px" }}>
            <div style={{ textAlign:"center", marginBottom:"20px" }}>
              <div style={{ fontSize:"44px", marginBottom:"12px" }}>🗑️</div>
              <h3 style={{ fontSize:"18px", fontWeight:"800", color:"#1C1917", marginBottom:"8px" }}>Move to Trash?</h3>
              <p style={{ fontSize:"14px", color:"#78716C", lineHeight:"1.6" }}>
                User will be moved to <strong>Trash</strong>.<br/>
                You can restore them anytime from the Trash page.
              </p>
            </div>
            <div style={{ display:"flex", gap:"10px" }}>
              <button onClick={() => setConfirmId(null)} style={{ flex:1, padding:"12px", borderRadius:"12px", border:"1.5px solid #E7E5E4", background:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"14px", fontWeight:"700", color:"#78716C", cursor:"pointer" }}>Cancel</button>
              <button onClick={() => trashUser(confirmId)} disabled={!!trashingId}
                style={{ flex:1, padding:"12px", borderRadius:"12px", border:"none", background:"#D97706", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"14px", fontWeight:"700", color:"#fff", cursor:"pointer", opacity:trashingId?.7:1 }}>
                {trashingId===confirmId?"Moving…":"Move to Trash"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="main-wrap" style={{ display:"flex", minHeight:"100vh", fontFamily:"'Plus Jakarta Sans',sans-serif", backgroundColor:"#FAF7F4" }}>
        <div className={`sidebar-overlay ${sidebarOpen?"open":""}`} onClick={() => setSidebarOpen(false)} />

        {/* SIDEBAR */}
        <aside className={`sidebar ${sidebarOpen?"open":""}`}>
          <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"28px", padding:"0 4px" }}>
            <div style={{ width:"36px", height:"36px", background:"linear-gradient(135deg,#92400E,#D97706)", borderRadius:"10px", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <span style={{ fontSize:"18px" }}>🪚</span>
            </div>
            <div>
              <p style={{ fontWeight:"800", fontSize:"15px", color:"#1C1917" }}>Craftigo</p>
              <p style={{ fontSize:"11px", color:"#A8A29E", fontWeight:"500" }}>Admin Panel</p>
            </div>
          </div>
          <p style={{ fontSize:"11px", fontWeight:"700", color:"#C4B5A4", letterSpacing:"0.7px", textTransform:"uppercase", padding:"0 6px", marginBottom:"6px" }}>Menu</p>
          <button className="nav-btn" onClick={() => { navigate("/admin/dashboard"); setSidebarOpen(false); }}>🏠 Dashboard</button>
          <button className="nav-btn active">👥 Users</button>
          <button className="nav-btn" onClick={() => { navigate("/admin/carpenter-labours"); setSidebarOpen(false); }}>🔗 Carpenter & Labour</button>
          <button className="nav-btn" onClick={() => { navigate("/admin/trash"); setSidebarOpen(false); }}>🗑️ Trash</button>
          <div style={{ flex:1 }} />
          <div style={{ padding:"12px", background:"#FEF3C7", borderRadius:"12px", border:"1px solid #FDE68A", marginBottom:"10px" }}>
            <p style={{ fontSize:"12px", fontWeight:"700", color:"#92400E" }}>🛡️ Admin Access</p>
            <p style={{ fontSize:"11px", color:"#B45309", marginTop:"2px" }}>Full permissions</p>
          </div>
          <button className="nav-btn danger" onClick={() => { localStorage.clear(); navigate("/login"); }}>🚪 Logout</button>
        </aside>

        <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>
          <div className="topbar">
            <button className="hamburger" onClick={() => setSidebarOpen(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <line x1="3" y1="6" x2="21" y2="6" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="12" x2="21" y2="12" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="18" x2="21" y2="18" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <span style={{ fontWeight:"800", fontSize:"16px", color:"#1C1917" }}>👥 Users</span>
          </div>

          <main className="main-content" style={{ padding:"28px 28px", flex:1, overflowY:"auto" }}>
            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"20px" }}>
              <div>
                <h1 style={{ fontSize:"24px", fontWeight:"800", color:"#1C1917", letterSpacing:"-0.5px" }}>All Users</h1>
                <p style={{ fontSize:"13px", color:"#A8A29E", marginTop:"3px" }}>
                  {loading?"Loading…":`${filteredUsers.length} of ${users.length} users`}
                </p>
              </div>
              <div style={{ display:"flex", gap:"8px" }}>
                <button onClick={() => navigate("/admin/trash")}
                  style={{ padding:"10px 14px", background:"#FEF2F2", border:"1.5px solid #FECACA", borderRadius:"12px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"13px", fontWeight:"700", color:"#DC2626", cursor:"pointer" }}>
                  🗑️ Trash
                </button>
                <button onClick={fetchUsers}
                  style={{ padding:"10px 14px", background:"#fff", border:"1.5px solid #E7E5E4", borderRadius:"12px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"13px", fontWeight:"700", color:"#44403C", cursor:"pointer" }}>
                  🔄 Refresh
                </button>
              </div>
            </div>

            {/* Active/Inactive status chips */}
            <div style={{ display:"flex", gap:"10px", marginBottom:"16px", flexWrap:"wrap" }}>
              {statusTabs.map(s => (
                <button key={s.key} onClick={() => setStatusFilter(s.key)}
                  style={{ display:"flex", alignItems:"center", gap:"7px", padding:"8px 14px", borderRadius:"20px",
                    border:`1.5px solid ${statusFilter===s.key?s.border:"#E7E5E4"}`,
                    background:statusFilter===s.key?s.bg:"#fff",
                    fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"12px", fontWeight:"700",
                    color:statusFilter===s.key?s.color:"#78716C", cursor:"pointer", transition:"all .15s" }}>
                  <span style={{ width:"7px", height:"7px", borderRadius:"50%", background:s.color, display:"inline-block" }} />
                  {s.label}
                  <span style={{ background:statusFilter===s.key?s.color:"#F3F4F6", color:statusFilter===s.key?"#fff":"#78716C", borderRadius:"20px", padding:"1px 7px", fontSize:"11px", fontWeight:"800" }}>{s.count}</span>
                </button>
              ))}
            </div>

            {/* Search + role tabs */}
            <div style={{ background:"#fff", border:"1.5px solid #E7E5E4", borderRadius:"16px", padding:"16px", marginBottom:"16px" }}>
              <div style={{ position:"relative", marginBottom:"14px" }}>
                <span style={{ position:"absolute", left:"13px", top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="#A8A29E" strokeWidth="2"/><path d="m21 21-4.35-4.35" stroke="#A8A29E" strokeWidth="2" strokeLinecap="round"/></svg>
                </span>
                <input className="search-input" type="text" placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
                {roleTabs.map(t => (
                  <button key={t.key} className={`tab-btn ${roleFilter===t.key?"active":""}`} onClick={() => setRoleFilter(t.key)}>
                    {t.label}
                    <span style={{ fontSize:"11px", fontWeight:"800", background:roleFilter===t.key?"#D97706":"#F3F4F6", color:roleFilter===t.key?"#fff":"#6B7280", borderRadius:"20px", padding:"1px 7px" }}>{t.count}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* DESKTOP TABLE */}
            <div className="desktop-table" style={{ background:"#fff", border:"1.5px solid #E7E5E4", borderRadius:"16px", overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"2fr 2fr 1fr 0.8fr 1.6fr", padding:"12px 20px", background:"#FAF7F4", borderBottom:"1.5px solid #E7E5E4" }}>
                {["Name","Email","Role","Status","Actions"].map(h => (
                  <span key={h} style={{ fontSize:"11px", fontWeight:"700", color:"#A8A29E", letterSpacing:"0.6px", textTransform:"uppercase" }}>{h}</span>
                ))}
              </div>

              {loading ? (
                <div style={{ padding:"48px", textAlign:"center" }}>
                  <div style={{ width:"32px", height:"32px", border:"3px solid #FDE68A", borderTop:"3px solid #D97706", borderRadius:"50%", animation:"spin .7s linear infinite", margin:"0 auto 12px" }} />
                  <p style={{ color:"#A8A29E", fontSize:"14px" }}>Loading users…</p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div style={{ padding:"48px", textAlign:"center" }}>
                  <p style={{ fontSize:"32px", marginBottom:"10px" }}>🔍</p>
                  <p style={{ color:"#78716C", fontWeight:"600", fontSize:"15px" }}>No users found</p>
                  <p style={{ color:"#A8A29E", fontSize:"13px", marginTop:"4px" }}>Try adjusting filters</p>
                </div>
              ) : (
                filteredUsers.map((u, i) => {
                  const rs = roleStyle(u.role);
                  const isActive = u.isActive !== false;
                  return (
                    <div key={u._id} className="user-row"
                      style={{ display:"grid", gridTemplateColumns:"2fr 2fr 1fr 0.8fr 1.6fr", padding:"13px 20px", borderBottom:i<filteredUsers.length-1?"1px solid #F5F5F4":"none", alignItems:"center", opacity:isActive?1:0.7 }}>

                      <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                        <div style={{ width:"34px", height:"34px", borderRadius:"50%", background:isActive?`${rs.color}22`:"#F5F5F4", border:`1.5px solid ${isActive?rs.border:"#E7E5E4"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"13px", fontWeight:"800", color:isActive?rs.color:"#A8A29E", flexShrink:0 }}>
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <span style={{ fontSize:"14px", fontWeight:"600", color:isActive?"#1C1917":"#A8A29E" }}>{u.name}</span>
                      </div>

                      <span style={{ fontSize:"13px", color:"#78716C", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.email}</span>

                      <span style={{ display:"inline-flex", padding:"3px 9px", borderRadius:"20px", background:rs.bg, border:`1px solid ${rs.border}`, color:rs.color, fontSize:"11px", fontWeight:"700", width:"fit-content", textTransform:"capitalize" }}>
                        {u.role}
                      </span>

                      <span style={{ display:"inline-flex", alignItems:"center", gap:"4px", padding:"3px 9px", borderRadius:"20px", fontSize:"11px", fontWeight:"700", width:"fit-content",
                        background:isActive?"#D1FAE5":"#FEF2F2", border:isActive?"1px solid #A7F3D0":"1px solid #FECACA", color:isActive?"#059669":"#DC2626" }}>
                        <span style={{ width:"5px", height:"5px", borderRadius:"50%", background:isActive?"#059669":"#DC2626", display:"inline-block" }} />
                        {isActive?"Active":"Inactive"}
                      </span>

                      {u.role==="admin" ? (
                        <span style={{ fontSize:"12px", fontWeight:"600", color:"#A8A29E", padding:"6px 10px", background:"#F9F9F9", borderRadius:"8px", border:"1px solid #E7E5E4", width:"fit-content" }}>🔒 Protected</span>
                      ) : (
                        <div style={{ display:"flex", gap:"5px", flexWrap:"wrap" }}>
                          {/* Edit */}
                          <button onClick={() => openEdit(u)}
                            style={{ padding:"6px 10px", borderRadius:"8px", border:"1.5px solid #FDE68A", background:"#FEF3C7", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"11px", fontWeight:"700", color:"#D97706", cursor:"pointer" }}
                            onMouseEnter={e=>{e.currentTarget.style.background="#D97706";e.currentTarget.style.color="#fff"}}
                            onMouseLeave={e=>{e.currentTarget.style.background="#FEF3C7";e.currentTarget.style.color="#D97706"}}>
                            ✏️ Edit
                          </button>
                          {/* Activate / Deactivate */}
                          <button onClick={() => toggleActive(u)} disabled={togglingId===u._id}
                            style={{ padding:"6px 10px", borderRadius:"8px", border:`1.5px solid ${isActive?"#FECACA":"#A7F3D0"}`, background:isActive?"#FEF2F2":"#D1FAE5", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"11px", fontWeight:"700", color:isActive?"#DC2626":"#059669", cursor:"pointer", opacity:togglingId===u._id?.6:1 }}>
                            {togglingId===u._id?"…":isActive?"🚫 Deactivate":"✅ Activate"}
                          </button>
                          {/* Trash — NOT delete */}
                          <button onClick={() => setConfirmId(u._id)} disabled={!!trashingId}
                            style={{ padding:"6px 10px", borderRadius:"8px", border:"1.5px solid #E7E5E4", background:"#F5F5F4", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"11px", fontWeight:"700", color:"#78716C", cursor:"pointer" }}
                            onMouseEnter={e=>{e.currentTarget.style.background="#FEF2F2";e.currentTarget.style.color="#DC2626";e.currentTarget.style.borderColor="#FECACA"}}
                            onMouseLeave={e=>{e.currentTarget.style.background="#F5F5F4";e.currentTarget.style.color="#78716C";e.currentTarget.style.borderColor="#E7E5E4"}}>
                            🗑 Trash
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* MOBILE CARDS */}
            <div className="mobile-cards">
              {loading ? (
                <div style={{ padding:"48px", textAlign:"center" }}>
                  <div style={{ width:"32px", height:"32px", border:"3px solid #FDE68A", borderTop:"3px solid #D97706", borderRadius:"50%", animation:"spin .7s linear infinite", margin:"0 auto" }} />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div style={{ padding:"48px", textAlign:"center" }}>
                  <p style={{ fontSize:"32px", marginBottom:"10px" }}>🔍</p>
                  <p style={{ color:"#78716C", fontWeight:"600" }}>No users found</p>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
                  {filteredUsers.map(u => {
                    const rs = roleStyle(u.role);
                    const isActive = u.isActive !== false;
                    return (
                      <div key={u._id} style={{ background:"#fff", border:"1.5px solid #E7E5E4", borderRadius:"14px", padding:"14px", display:"flex", alignItems:"center", gap:"12px", opacity:isActive?1:0.75 }}>
                        <div style={{ width:"42px", height:"42px", borderRadius:"50%", background:isActive?`${rs.color}22`:"#F5F5F4", border:`1.5px solid ${isActive?rs.border:"#E7E5E4"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"16px", fontWeight:"800", color:isActive?rs.color:"#A8A29E", flexShrink:0 }}>
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <p style={{ fontSize:"14px", fontWeight:"700", color:isActive?"#1C1917":"#A8A29E", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.name}</p>
                          <p style={{ fontSize:"12px", color:"#78716C", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.email}</p>
                          <div style={{ display:"flex", gap:"5px", marginTop:"5px", flexWrap:"wrap" }}>
                            <span style={{ padding:"2px 8px", borderRadius:"20px", background:rs.bg, border:`1px solid ${rs.border}`, color:rs.color, fontSize:"10px", fontWeight:"700", textTransform:"capitalize" }}>{u.role}</span>
                            <span style={{ padding:"2px 8px", borderRadius:"20px", fontSize:"10px", fontWeight:"700", background:isActive?"#D1FAE5":"#FEF2F2", border:isActive?"1px solid #A7F3D0":"1px solid #FECACA", color:isActive?"#059669":"#DC2626" }}>
                              {isActive?"Active":"Inactive"}
                            </span>
                          </div>
                        </div>
                        {u.role!=="admin" && (
                          <div style={{ display:"flex", flexDirection:"column", gap:"5px", flexShrink:0 }}>
                            <button onClick={() => openEdit(u)} style={{ padding:"6px 8px", borderRadius:"8px", border:"1.5px solid #FDE68A", background:"#FEF3C7", fontSize:"12px", fontWeight:"700", color:"#D97706", cursor:"pointer" }}>✏️</button>
                            <button onClick={() => toggleActive(u)} disabled={togglingId===u._id}
                              style={{ padding:"6px 8px", borderRadius:"8px", border:`1.5px solid ${isActive?"#FECACA":"#A7F3D0"}`, background:isActive?"#FEF2F2":"#D1FAE5", fontSize:"12px", cursor:"pointer" }}>
                              {togglingId===u._id?"…":isActive?"🚫":"✅"}
                            </button>
                            <button onClick={() => setConfirmId(u._id)}
                              style={{ padding:"6px 8px", borderRadius:"8px", border:"1.5px solid #E7E5E4", background:"#F5F5F4", fontSize:"12px", fontWeight:"700", color:"#78716C", cursor:"pointer" }}>🗑</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}